import React from 'react';
import { ArrowLeft, Lock, LogIn, Mail, ShieldCheck } from 'lucide-react';

const Auth = ({
    authClient,
    email, setEmail,
    password, setPassword,
    handleAuth,
    mfaFlow,
    mfaCode,
    setMfaCode,
    trustMfaDevice,
    setTrustMfaDevice,
    handleMfaVerify,
    onMfaCancel,
    loginError,
    authNotice,
    isLoggingIn,
    isDarkMode,
    authMode,
    setAuthMode,
    inviteToken,
    inviteMessage,
    onBackToLanding
}) => {
    const canCreateAccount = Boolean(inviteToken);
    const isMfaStep = Boolean(mfaFlow?.challengeToken);
    const [isPasswordInputReady, setIsPasswordInputReady] = React.useState(false);
    const [recoveryStep, setRecoveryStep] = React.useState('login');
    const [recoveryChallengeToken, setRecoveryChallengeToken] = React.useState('');
    const [recoveryCode, setRecoveryCode] = React.useState('');
    const [recoveryPassword, setRecoveryPassword] = React.useState('');
    const [recoveryPasswordConfirm, setRecoveryPasswordConfirm] = React.useState('');
    const [recoveryError, setRecoveryError] = React.useState('');
    const [recoveryNotice, setRecoveryNotice] = React.useState('');
    const [recoveryBusy, setRecoveryBusy] = React.useState(false);

    const returnToLogin = () => {
        setRecoveryStep('login');
        setRecoveryChallengeToken('');
        setRecoveryCode('');
        setRecoveryPassword('');
        setRecoveryPasswordConfirm('');
        setRecoveryError('');
        setRecoveryNotice('');
    };

    const handleRecoveryRequest = async (event) => {
        event.preventDefault();
        setRecoveryError('');
        setRecoveryNotice('');
        if (!authClient?.requestPasswordReset) {
            setRecoveryError('Passwort-Wiederherstellung ist nicht verfügbar.');
            return;
        }
        setRecoveryBusy(true);
        try {
            const { data, error } = await authClient.requestPasswordReset(email);
            if (error) throw error;
            setRecoveryChallengeToken(data?.challengeToken || '');
            setRecoveryStep('confirm');
            setRecoveryNotice(data?.message || 'Wenn ein Konto existiert, wurde ein Code per E-Mail gesendet.');
        } catch (error) {
            setRecoveryError(error.message || 'Der Wiederherstellungscode konnte nicht angefordert werden.');
        } finally {
            setRecoveryBusy(false);
        }
    };

    const handleRecoveryConfirm = async (event) => {
        event.preventDefault();
        setRecoveryError('');
        if (!/^\d{6}$/.test(recoveryCode)) {
            setRecoveryError('Bitte gib den sechsstelligen Code aus der E-Mail ein.');
            return;
        }
        if (recoveryPassword.length < 12) {
            setRecoveryError('Das Passwort muss mindestens 12 Zeichen haben.');
            return;
        }
        if (recoveryPassword !== recoveryPasswordConfirm) {
            setRecoveryError('Die Passwörter stimmen nicht überein.');
            return;
        }
        setRecoveryBusy(true);
        try {
            const { error } = await authClient.confirmPasswordReset({
                challengeToken: recoveryChallengeToken,
                code: recoveryCode,
                password: recoveryPassword
            });
            if (error) throw error;
            setPassword('');
            setRecoveryStep('success');
            setRecoveryNotice('Dein Passwort wurde geändert. Alle bisherigen Sitzungen wurden beendet.');
        } catch (error) {
            setRecoveryError(error.message || 'Passwort konnte nicht zurückgesetzt werden.');
        } finally {
            setRecoveryBusy(false);
        }
    };
    const pageBg = isDarkMode ? 'bg-[#070C12] text-white' : 'bg-[#f5f7fb] text-slate-950';
    const panel = isDarkMode ? 'border-[#1F2A36] bg-[#0D151F]/94' : 'border-slate-200 bg-white/95';
    const labelText = isDarkMode ? 'text-slate-300' : 'text-slate-700';
    const mutedText = isDarkMode ? 'text-slate-400' : 'text-slate-500';
    const fieldBg = isDarkMode
        ? 'border-[#243247] bg-[#09111A] text-white placeholder:text-slate-600'
        : 'border-slate-200 bg-white text-slate-950 placeholder:text-slate-400';
    const logoText = isDarkMode ? 'text-white' : 'text-slate-950';
    const iconText = isDarkMode ? 'text-slate-500' : 'text-slate-400';
    const secondaryButton = isDarkMode
        ? 'border-[#243247] bg-[#0D151F] text-slate-300 hover:bg-[#111C2A] hover:text-white'
        : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50 hover:text-slate-950';
    const modeInactive = isDarkMode ? 'text-slate-400 hover:bg-white/[0.05] hover:text-white' : 'text-slate-500 hover:bg-white hover:text-slate-950';

    return (
        <div className={`min-h-screen ${pageBg}`} style={{ fontFamily: 'Satoshi, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, Segoe UI, sans-serif' }}>
            <div className="pointer-events-none fixed inset-0 bg-[radial-gradient(circle_at_18%_12%,rgba(229,92,26,0.12),transparent_30%),radial-gradient(circle_at_84%_80%,rgba(15,23,42,0.08),transparent_28%)]" />
            <div
                className="pointer-events-none fixed inset-0 opacity-[0.34]"
                style={{
                    backgroundImage: 'linear-gradient(90deg, rgba(100,116,139,0.10) 1px, transparent 1px), linear-gradient(180deg, rgba(100,116,139,0.08) 1px, transparent 1px)',
                    backgroundSize: '58px 58px',
                }}
            />

            <div className="relative mx-auto flex min-h-screen w-full max-w-[1180px] flex-col px-5 py-6 sm:px-7 lg:px-8">
                <header className="flex items-center justify-between gap-4">
                    <div className="flex min-w-0 items-center">
                        <img src={isDarkMode ? "/commetriq-logo-dark.webp" : "/commetriq-logo-light.webp"} alt="commetriq Logo" className="h-12 w-auto max-w-[240px] shrink-0 object-contain sm:h-14" />
                    </div>
                    {onBackToLanding && (
                        <button
                            type="button"
                            onClick={onBackToLanding}
                            className={`inline-flex items-center gap-2 rounded-lg border px-4 py-2.5 text-sm font-semibold transition-colors ${secondaryButton}`}
                        >
                            <ArrowLeft className="h-4 w-4" />
                            Zur Website
                        </button>
                    )}
                </header>

                <main className="flex flex-1 items-center justify-center py-10">
                    <section className={`w-full max-w-[580px] rounded-lg border p-7 shadow-[0_24px_70px_rgba(15,23,42,0.12)] backdrop-blur md:p-9 ${panel}`}>
                        <div className="mb-8 text-center">
                            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-lg bg-[#E55C1A]/10 text-[#E55C1A]">
                                <ShieldCheck className="h-6 w-6" />
                            </div>
                            <h1 className={`mt-5 text-3xl font-semibold tracking-[-0.05em] ${logoText}`}>
                                {isMfaStep
                                    ? 'E-Mail bestätigen'
                                    : recoveryStep === 'request'
                                        ? 'Passwort zurücksetzen'
                                        : recoveryStep === 'confirm'
                                            ? 'Code bestätigen'
                                            : recoveryStep === 'success'
                                                ? 'Passwort geändert'
                                                : (authMode === 'signUp' ? 'Konto erstellen' : 'Einloggen')}
                            </h1>
                            <p className={`mt-2 text-base ${mutedText}`}>
                                {isMfaStep
                                    ? `Wir haben einen Anmeldecode an ${mfaFlow?.maskedEmail || 'deine hinterlegte E-Mail-Adresse'} gesendet.`
                                    : recoveryStep === 'request'
                                        ? 'Wir senden dir einen einmaligen Code per E-Mail.'
                                        : recoveryStep === 'confirm'
                                            ? 'Gib den Code und dein neues Passwort ein.'
                                            : recoveryStep === 'success'
                                                ? 'Du kannst dich jetzt mit dem neuen Passwort anmelden.'
                                                : 'Melde dich in deinem Dashboard an.'}
                            </p>
                        </div>

                        {inviteToken && (
                            <div className={`mb-5 rounded-lg border px-4 py-3 text-sm ${isDarkMode ? 'border-emerald-700 bg-emerald-900/20 text-emerald-300' : 'border-emerald-200 bg-emerald-50 text-emerald-700'}`}>
                                {inviteMessage || 'Einladung erkannt. Erstelle dein Konto oder melde dich an.'}
                            </div>
                        )}

                        {canCreateAccount && !isMfaStep && recoveryStep === 'login' && (
                            <div className={`mb-6 grid grid-cols-2 rounded-lg border p-1 ${isDarkMode ? 'border-[#243247] bg-[#09111A]' : 'border-slate-200 bg-slate-100'}`}>
                                <button
                                    type="button"
                                    onClick={() => setAuthMode('signIn')}
                                    className={`rounded-md px-3 py-2.5 text-sm font-semibold transition-colors ${authMode === 'signIn' ? 'bg-[#E55C1A] text-white shadow-sm shadow-[#E55C1A]/30' : modeInactive}`}
                                >
                                    Anmelden
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setAuthMode('signUp')}
                                    className={`rounded-md px-3 py-2.5 text-sm font-semibold transition-colors ${authMode === 'signUp' ? 'bg-[#E55C1A] text-white shadow-sm shadow-[#E55C1A]/30' : modeInactive}`}
                                >
                                    Konto erstellen
                                </button>
                            </div>
                        )}

                        {isMfaStep ? (
                            <form onSubmit={handleMfaVerify} className="grid gap-5">
                                <div>
                                    <label htmlFor="mfa-code" className={`mb-2 block text-sm font-semibold ${labelText}`}>Code aus der E-Mail</label>
                                    <input
                                        id="mfa-code"
                                        type="text"
                                        inputMode="numeric"
                                        pattern="[0-9]{6}"
                                        maxLength={6}
                                        placeholder="123456"
                                        value={mfaCode}
                                        onChange={(event) => setMfaCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
                                        className={`h-14 w-full rounded-lg border px-4 text-center text-xl font-bold tracking-[0.35em] outline-none transition focus:border-[#E55C1A] focus:ring-4 focus:ring-[#E55C1A]/15 ${fieldBg}`}
                                        disabled={isLoggingIn}
                                        required
                                        autoFocus
                                        autoComplete="one-time-code"
                                    />
                                </div>

                                <label className={`flex cursor-pointer items-start gap-3 rounded-lg border px-4 py-3 ${fieldBg}`}>
                                    <input
                                        type="checkbox"
                                        checked={trustMfaDevice}
                                        onChange={(event) => setTrustMfaDevice(event.target.checked)}
                                        disabled={isLoggingIn}
                                        className="mt-0.5 h-4 w-4 accent-[#E55C1A]"
                                    />
                                    <span>
                                        <span className={`block text-sm font-semibold ${labelText}`}>Diesem Browser 30 Tage vertrauen</span>
                                        <span className={`mt-1 block text-xs ${mutedText}`}>Auf diesem Browser wird der E-Mail-Code in dieser Zeit nicht erneut abgefragt.</span>
                                    </span>
                                </label>

                                {loginError && (
                                    <div className={`rounded-lg border px-4 py-3 text-sm font-medium ${isDarkMode ? 'border-red-500/25 bg-red-500/10 text-red-300' : 'border-red-200 bg-red-50 text-red-700'}`}>
                                        {loginError}
                                    </div>
                                )}

                                <button
                                    type="submit"
                                    disabled={isLoggingIn || mfaCode.length !== 6}
                                    className={`mt-1 flex h-14 items-center justify-center gap-2 rounded-lg bg-[#E55C1A] px-4 text-base font-semibold text-white shadow-[0_16px_34px_rgba(229,92,26,0.22)] transition-all hover:-translate-y-0.5 hover:bg-[#d14f10] active:scale-[0.98] ${isLoggingIn || mfaCode.length !== 6 ? 'cursor-not-allowed opacity-70' : ''}`}
                                >
                                    <ShieldCheck size={18} />
                                    {isLoggingIn ? 'Prüfe...' : 'Anmelden'}
                                </button>
                                <button type="button" onClick={onMfaCancel} disabled={isLoggingIn} className={`h-12 rounded-lg border text-sm font-semibold transition-colors ${secondaryButton}`}>
                                    Zurück zum Login
                                </button>
                            </form>
                        ) : recoveryStep === 'request' ? (
                            <form onSubmit={handleRecoveryRequest} className="grid gap-5">
                                <div>
                                    <label htmlFor="recovery-email" className={`mb-2 block text-sm font-semibold ${labelText}`}>E-Mail-Adresse</label>
                                    <div className="relative">
                                        <Mail size={18} className={`pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 ${iconText}`} />
                                        <input
                                            id="recovery-email"
                                            name="username"
                                            type="email"
                                            value={email}
                                            onChange={(event) => setEmail(event.target.value)}
                                            className={`h-14 w-full rounded-lg border px-11 text-base outline-none transition focus:border-[#E55C1A] focus:ring-4 focus:ring-[#E55C1A]/15 ${fieldBg}`}
                                            disabled={recoveryBusy}
                                            required
                                            autoComplete="username"
                                            autoFocus
                                        />
                                    </div>
                                </div>
                                {recoveryError && <div className={`rounded-lg border px-4 py-3 text-sm font-medium ${isDarkMode ? 'border-red-500/25 bg-red-500/10 text-red-300' : 'border-red-200 bg-red-50 text-red-700'}`}>{recoveryError}</div>}
                                <button type="submit" disabled={recoveryBusy} className={`flex h-14 items-center justify-center gap-2 rounded-lg bg-[#E55C1A] px-4 text-base font-semibold text-white transition-all hover:bg-[#d14f10] ${recoveryBusy ? 'cursor-not-allowed opacity-70' : ''}`}>
                                    <Mail size={18} />
                                    {recoveryBusy ? 'Sende...' : 'Code per E-Mail senden'}
                                </button>
                                <button type="button" onClick={returnToLogin} disabled={recoveryBusy} className={`h-12 rounded-lg border text-sm font-semibold transition-colors ${secondaryButton}`}>Zurück zum Login</button>
                            </form>
                        ) : recoveryStep === 'confirm' ? (
                            <form onSubmit={handleRecoveryConfirm} className="grid gap-5">
                                {recoveryNotice && <div className={`rounded-lg border px-4 py-3 text-sm font-medium ${isDarkMode ? 'border-emerald-500/25 bg-emerald-500/10 text-emerald-300' : 'border-emerald-200 bg-emerald-50 text-emerald-700'}`}>{recoveryNotice}</div>}
                                <div>
                                    <label htmlFor="recovery-code" className={`mb-2 block text-sm font-semibold ${labelText}`}>Code aus der E-Mail</label>
                                    <input id="recovery-code" type="text" inputMode="numeric" pattern="[0-9]{6}" maxLength={6} value={recoveryCode} onChange={(event) => setRecoveryCode(event.target.value.replace(/\D/g, '').slice(0, 6))} className={`h-14 w-full rounded-lg border px-4 text-center text-xl font-bold tracking-[0.35em] outline-none transition focus:border-[#E55C1A] focus:ring-4 focus:ring-[#E55C1A]/15 ${fieldBg}`} disabled={recoveryBusy} required autoComplete="one-time-code" autoFocus />
                                </div>
                                <div>
                                    <label htmlFor="recovery-password" className={`mb-2 block text-sm font-semibold ${labelText}`}>Neues Passwort</label>
                                    <input id="recovery-password" name="new-password" type="password" value={recoveryPassword} onChange={(event) => setRecoveryPassword(event.target.value)} className={`h-14 w-full rounded-lg border px-4 text-base outline-none transition focus:border-[#E55C1A] focus:ring-4 focus:ring-[#E55C1A]/15 ${fieldBg}`} minLength={12} maxLength={256} disabled={recoveryBusy} required autoComplete="new-password" />
                                </div>
                                <div>
                                    <label htmlFor="recovery-password-confirm" className={`mb-2 block text-sm font-semibold ${labelText}`}>Passwort bestätigen</label>
                                    <input id="recovery-password-confirm" name="new-password-confirm" type="password" value={recoveryPasswordConfirm} onChange={(event) => setRecoveryPasswordConfirm(event.target.value)} className={`h-14 w-full rounded-lg border px-4 text-base outline-none transition focus:border-[#E55C1A] focus:ring-4 focus:ring-[#E55C1A]/15 ${fieldBg}`} minLength={12} maxLength={256} disabled={recoveryBusy} required autoComplete="new-password" />
                                </div>
                                {recoveryError && <div className={`rounded-lg border px-4 py-3 text-sm font-medium ${isDarkMode ? 'border-red-500/25 bg-red-500/10 text-red-300' : 'border-red-200 bg-red-50 text-red-700'}`}>{recoveryError}</div>}
                                <button type="submit" disabled={recoveryBusy || recoveryCode.length !== 6} className={`flex h-14 items-center justify-center gap-2 rounded-lg bg-[#E55C1A] px-4 text-base font-semibold text-white transition-all hover:bg-[#d14f10] ${recoveryBusy || recoveryCode.length !== 6 ? 'cursor-not-allowed opacity-70' : ''}`}>
                                    <ShieldCheck size={18} />
                                    {recoveryBusy ? 'Prüfe...' : 'Passwort speichern'}
                                </button>
                                <button type="button" onClick={() => setRecoveryStep('request')} disabled={recoveryBusy} className={`h-12 rounded-lg border text-sm font-semibold transition-colors ${secondaryButton}`}>Neuen Code anfordern</button>
                            </form>
                        ) : recoveryStep === 'success' ? (
                            <div className="grid gap-5">
                                <div className={`rounded-lg border px-4 py-4 text-sm font-medium ${isDarkMode ? 'border-emerald-500/25 bg-emerald-500/10 text-emerald-300' : 'border-emerald-200 bg-emerald-50 text-emerald-700'}`}>{recoveryNotice}</div>
                                <button type="button" onClick={returnToLogin} className="flex h-14 items-center justify-center gap-2 rounded-lg bg-[#E55C1A] px-4 text-base font-semibold text-white transition-all hover:bg-[#d14f10]">
                                    <LogIn size={18} /> Zum Login
                                </button>
                            </div>
                        ) : (
                            <form onSubmit={handleAuth} className="grid gap-5" autoComplete="on">
                                <div>
                                    <label htmlFor="auth-email" className={`mb-2 block text-sm font-semibold ${labelText}`}>E-Mail-Adresse</label>
                                    <div className="relative">
                                        <Mail size={18} className={`pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 ${iconText}`} />
                                        <input
                                            id="auth-email"
                                            name="username"
                                            type="email"
                                            placeholder="du@firma.de"
                                            value={email}
                                            onChange={(event) => setEmail(event.target.value)}
                                            className={`h-14 w-full rounded-lg border px-11 text-base outline-none transition focus:border-[#E55C1A] focus:ring-4 focus:ring-[#E55C1A]/15 ${fieldBg}`}
                                            disabled={isLoggingIn}
                                            required
                                            autoComplete="username"
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label htmlFor="auth-password" className={`mb-2 block text-sm font-semibold ${labelText}`}>Passwort</label>
                                    <div className="relative">
                                        <Lock size={18} className={`pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 ${iconText}`} />
                                        <input
                                            id="auth-password"
                                            name="password"
                                            type="password"
                                            placeholder="Passwort"
                                            value={password}
                                            onChange={(event) => setPassword(event.target.value)}
                                            onFocus={() => setIsPasswordInputReady(true)}
                                            readOnly={!isPasswordInputReady}
                                            className={`h-14 w-full rounded-lg border px-11 text-base outline-none transition focus:border-[#E55C1A] focus:ring-4 focus:ring-[#E55C1A]/15 ${fieldBg}`}
                                            disabled={isLoggingIn}
                                            required
                                            autoComplete={authMode === 'signUp' ? 'new-password' : 'current-password'}
                                        />
                                    </div>
                                    {authMode === 'signIn' && (
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setRecoveryError('');
                                                setRecoveryNotice('');
                                                setRecoveryStep('request');
                                            }}
                                            className="mt-2 text-sm font-semibold text-[#E55C1A] hover:text-[#f1763a]"
                                        >
                                            Passwort vergessen?
                                        </button>
                                    )}
                                </div>

                                {loginError && (
                                    <div className={`rounded-lg border px-4 py-3 text-sm font-medium ${isDarkMode ? 'border-red-500/25 bg-red-500/10 text-red-300' : 'border-red-200 bg-red-50 text-red-700'}`}>
                                        {loginError}
                                    </div>
                                )}

                                {authNotice && (
                                    <div className={`rounded-lg border px-4 py-3 text-sm font-medium ${isDarkMode ? 'border-emerald-500/25 bg-emerald-500/10 text-emerald-300' : 'border-emerald-200 bg-emerald-50 text-emerald-700'}`}>
                                        {authNotice}
                                    </div>
                                )}

                                <button
                                    type="submit"
                                    disabled={isLoggingIn}
                                    className={`mt-1 flex h-14 items-center justify-center gap-2 rounded-lg bg-[#E55C1A] px-4 text-base font-semibold text-white shadow-[0_16px_34px_rgba(229,92,26,0.22)] transition-all hover:-translate-y-0.5 hover:bg-[#d14f10] active:scale-[0.98] ${isLoggingIn ? 'cursor-not-allowed opacity-70' : ''}`}
                                >
                                    {isLoggingIn ? (
                                        <svg className="h-4 w-4 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                        </svg>
                                    ) : (
                                        <LogIn size={18} />
                                    )}
                                    {isLoggingIn ? 'Bitte warten...' : (authMode === 'signUp' ? 'Konto erstellen' : 'Einloggen')}
                                </button>
                            </form>
                        )}

                        <p className={`mt-6 border-t border-slate-500/10 pt-5 text-center text-xs ${mutedText}`}>
                            Zugang nur für berechtigte Nutzer.
                        </p>
                        <div className={`mt-3 flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-xs font-semibold ${mutedText}`}>
                            <a href="/impressum" className="hover:text-[#E55C1A]">Impressum</a>
                            <a href="/datenschutz" className="hover:text-[#E55C1A]">Datenschutz</a>
                            <a href="/agb" className="hover:text-[#E55C1A]">B2B-AGB</a>
                            <a href="/avv" className="hover:text-[#E55C1A]">AVV</a>
                        </div>
                    </section>
                </main>
            </div>
        </div>
    );
};

export default Auth;
